import { configureStore, createSelector } from '@reduxjs/toolkit';
import { createReduxEnhancer } from '@sentry/react';
import _ from 'lodash';

import type { PreloadedState } from './types';
import { rootReducer, type RootState } from './rootReducer';

import {
  abandonedHearingCallTimerMiddleware,
  audioFlowMiddleware,
  callStateResetBoundaryMiddleware,
} from 'features/call/call-base/middleware/';
import { callCaptionsListenerMiddleware } from 'features/call/call-captions/middleware';
import { stateTransitionMiddleware } from 'features/call/call-ui-state/middleware';
import { env } from 'features/environment/constants';
import { remoteMediaListenerMiddleware } from 'features/remote-media/store';

export { rootReducer, type RootState } from './rootReducer';

const sentryReduxEnhancer = createReduxEnhancer({
  configureScopeWithState: (scope, state) => {
    const { call, user, session, voiceSession } = state;
    const neededTagsMapping = {
      callId: call?.id,
      sipCallId: call?.sipCallId,
      email: user?.user?.email,
      rnsConnectionId: session?.rnsConnectionId,
      sessionId: session?.sessionId,
      voiceSessionId: voiceSession?.sessionId,
    };
    Object.entries(neededTagsMapping).forEach(([tag, value]) => {
      if (value) {
        scope.setTag(tag, value);
      }
    });
  },
});

export const setupStore = (preloadedState?: PreloadedState<RootState>) => {
  return configureStore({
    preloadedState,
    reducer: rootReducer,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        immutableCheck: false,
      })
        .prepend(
          remoteMediaListenerMiddleware,
          stateTransitionMiddleware,
          callStateResetBoundaryMiddleware,
          audioFlowMiddleware,
          callCaptionsListenerMiddleware
        )
        .concat(abandonedHearingCallTimerMiddleware),
    enhancers: (getDefaultEnhancers) =>
      getDefaultEnhancers().concat(sentryReduxEnhancer),
    devTools: env.isDevelopmentBuild && {
      maxAge: 200,
    },
  });
};

/**
 * An optional createSelector, with more type checking.
 * Pass the input selectors as an array, not separate arguments.
 */
export const createAppSelector = createSelector.withTypes<RootState>();

/**
 * Guarantees that only one Redux store will be set up,
 * and the same store will always be returned.
 *
 * (The only exception is during local development,
 * since hot module replacement will cause this to be redefined
 * each time the dev server performs HMR.)
 */
export const setupStoreOnce = _.once(setupStore);

export type AppStore = ReturnType<typeof setupStore>;
export type AppDispatch = AppStore['dispatch'];
